import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChartLine, TrendUp } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import client from "../lib/api";

/**
 * 12-week rolling win-rate + volume trend chart for /app/loadboard/analytics.
 * Recharts LineChart with dual series — bids/week (light) + win-rate% (bold).
 * Hides itself when there's no bid history to plot.
 *
 * When `brokerFilter` is passed, the chart re-queries scoped to that broker
 * (drilling in from the "Filter by broker" dropdown at the top of the page).
 */
export default function AnalyticsTrendChart({ brokerFilter = "" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams({ weeks: "12" });
    if (brokerFilter) params.append("broker_company", brokerFilter);
    client.get(`/loadboard/analytics/timeseries?${params.toString()}`)
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && toast.error(e?.response?.data?.detail || "Trend chart failed"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [brokerFilter]);

  if (loading) {
    return (
      <div className="card-surface p-6" data-testid="trend-chart-loading">
        <div className="animate-pulse text-neutral-500 text-sm">Loading trend…</div>
      </div>
    );
  }
  if (!data) return null;

  const totalBids = data.series.reduce((s, w) => s + w.bids, 0);
  // When filtered to a broker with no historical bids, show an empty state
  // instead of a totally-flat zero-line chart. Global view still hides itself
  // (no signal to show yet).
  if (totalBids === 0) {
    if (brokerFilter) {
      return (
        <div className="card-surface p-6" data-testid="analytics-trend-chart-empty">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
            <ChartLine size={13} />
            12-Week Trend · {brokerFilter}
          </div>
          <div className="text-sm text-neutral-400 mt-2">
            No bids on record with <b className="text-white">{brokerFilter}</b> in the last 12 weeks.
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="card-surface p-6" data-testid="analytics-trend-chart">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
            <ChartLine size={13} />
            12-Week Trend
            {brokerFilter && (
              <span className="text-[#FF5A1F] normal-case tracking-normal" data-testid="trend-chart-broker-tag">
                · {brokerFilter}
              </span>
            )}
          </div>
          <h3 className="font-display text-lg font-semibold text-white">
            Bidding volume & win rate
          </h3>
        </div>
        <div className="flex items-center gap-4 text-[11px]">
          <span className="flex items-center gap-1.5 text-neutral-400">
            <span className="w-3 h-0.5 bg-white/40 rounded-full" />
            Bids/week
          </span>
          <span className="flex items-center gap-1.5 text-[#FF5A1F]">
            <span className="w-3 h-0.5 bg-[#FF5A1F] rounded-full" />
            Win rate %
          </span>
        </div>
      </div>

      <div className="h-56 sm:h-64 -mx-2" data-testid="trend-chart-canvas">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data.series} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis
              dataKey="week_short"
              stroke="#666"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
            />
            <YAxis
              yAxisId="bids"
              stroke="#666"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
              width={30}
            />
            <YAxis
              yAxisId="rate"
              orientation="right"
              stroke="#FF5A1F"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,90,31,0.2)" }}
              domain={[0, 100]}
              unit="%"
              width={40}
            />
            <Tooltip
              contentStyle={{
                background: "rgba(10,10,10,0.95)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 8, fontSize: 12, color: "#fff",
              }}
              labelStyle={{ color: "#999", fontSize: 11, marginBottom: 4 }}
              formatter={(value, name) => {
                if (name === "Win rate") return [value !== null && value !== undefined ? `${value}%` : "—", name];
                return [value, name];
              }}
            />
            <Line
              yAxisId="bids"
              type="monotone"
              dataKey="bids"
              name="Bids"
              stroke="rgba(255,255,255,0.4)"
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 4, stroke: "#fff", strokeWidth: 2, fill: "#000" }}
            />
            <Line
              yAxisId="rate"
              type="monotone"
              dataKey="win_rate_pct"
              name="Win rate"
              stroke="#FF5A1F"
              strokeWidth={2}
              dot={{ r: 3, fill: "#FF5A1F", stroke: "#000", strokeWidth: 2 }}
              activeDot={{ r: 5, stroke: "#000", strokeWidth: 2, fill: "#FF5A1F" }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
